import Vue from "vue";
import Router from "vue-router";

import UserStore from "../../user_dashboard/components/User/UserStore";
import SidebarStore from "../components/Sidebar/Store";
import CompanyStore from "../../shared/components/Company/CompanyStore";
import SetupStore from "../../shared/components/Setup/SetupStore";
import ProgressBarStore from "../../shared/ProgressBarStore";

import { InvoiceEdit, InvoiceNew, InvoiceShow, InvoiceReceipt, InvoicePayout } from "../components/Invoices";
import { Welcome } from "../components/Welcome";
import { CustomerIndex, CustomerNew, CustomerEdit, CustomerShow } from "../components/Customer";
import { VoucherTemplateIndex, VoucherTemplateNew, VoucherTemplateEdit } from "../components/VoucherTemplates";
import { UserEdit } from "../../user_dashboard/components/User";
import { UserEmailNew, UserEmailEdit } from "../../user_dashboard/components/UserEmail";
import { UserMobileNumberNew, UserMobileNumberEdit } from "../../user_dashboard/components/UserMobileNumber";
import { PayoutNew } from "../../shared/components/Payout";
import { UserPasswordEdit } from "../../user_dashboard/components/UserPassword";
import { CompanyEdit, CompanyNew } from "../../shared/components/Company";
import { MerchantAccountEdit } from "../../shared/components/MerchantAccount";
import { SettingsEdit } from "../../shared/components/Settings";
import { EmpowermentIndex, EmpowermentNew, EmpowermentEdit } from "../../shared/components/Empowerments";

import {
  InvoiceReportRevenuesReceivables,
  InvoiceReportExpensesPayables
} from "../components/InvoiceReport";

Vue.use(Router);

const router = new Router({
  routes: [

    // Default
    {
      path: "/",
      component: Welcome,
      beforeEnter: (to, from, next) => {
        ProgressBarStore.methods.cancelLoadingTimer();

        if (CompanyStore.currentCompany.id) {
          next({ name: "New Invoice" });
        } else if (UserStore.state.user.invoices_count) {
          next({ name: "Expenses" });
        } else {
          next();
        }
      }
    },

    // Invoice
    {
      path: "/invoices/new",
      name: "New Invoice",
      component: InvoiceNew,
      beforeEnter: (to, from, next) => {
        if (CompanyStore.currentCompany.id) {
          if (CompanyStore.currentCompany.legal_name && CompanyStore.currentCompany.base_currency_id) {
            next();
          } else {
            Vue.notify({
              group: "notices",
              type: "error",
              text: "To create an invoice your company must have a legal name and a default currency."
            });
            next({ path: `/merchant/${CompanyStore.currentCompany.id}/account` });
          }
        } else {
          Vue.notify({
            group: "notices",
            type: "error",
            text: "To create an invoice you must create a company profile with a legal name."
          });
          next({ name: "New company" });
        }
      }
    },
    {
      path: "/invoices/:hash/edit",
      name: "Edit Invoice",
      component: InvoiceEdit
    },
    {
      path: "/invoices/:hash",
      name: "Show Invoice",
      component: InvoiceShow
    },
    {
      path: "/invoices/:hash/receipt",
      name: "Invoice Receipt",
      component: InvoiceReceipt
    },
    {
      path: "/invoices/:hash/payout",
      name: "Invoice Payout",
      component: InvoicePayout
    },
    {
      path: "/templates/new",
      name: "New Template",
      component: VoucherTemplateNew,
      beforeEnter: (to, from, next) => {
        ProgressBarStore.methods.cancelLoadingTimer();

        if (CompanyStore.currentCompany.base_currency_id) {
          next();
        } else {
          Vue.notify({
            group: "notices",
            type: "error",
            text: "Please set a default currency."
          });
          next({ path: `/merchant/${CompanyStore.currentCompany.id}/account` });
        }
      }
    },
    {
      path: "/templates/:id/edit",
      name: "Edit Template",
      component: VoucherTemplateEdit,
      beforeEnter: (to, from, next) => {
        ProgressBarStore.methods.cancelLoadingTimer();

        if (CompanyStore.currentCompany.base_currency_id) {
          next();
        } else {
          Vue.notify({
            group: "notices",
            type: "error",
            text: "Please set a default currency."
          });
          next({ path: `/merchant/${CompanyStore.currentCompany.id}/account` });
        }
      }
    },
    {
      path: "/templates",
      name: "All Templates",
      component: VoucherTemplateIndex
    },

    // Customer
    {
      path: "/customers",
      name: "All Customers",
      component: CustomerIndex
    },
    {
      path: "/customers/new",
      name: "New Customer",
      component: CustomerNew
    },
    {
      path: "/customers/:id/edit",
      name: "Edit Customer",
      component: CustomerEdit
    },
    {
      path: "/customers/:id",
      name: "Show Customer",
      component: CustomerShow
    },

    // User Dashboard
    {
      path: "/user/:id/edit",
      name: "Edit User",
      component: UserEdit
    },
    {
      path: "/user/:id/password",
      name: "User Password",
      component: UserPasswordEdit,
      beforeEnter: async (to, from, next) => {
        await UserStore.ensureCurrentUser(to.params.id);
        next();
      }
    },

    {
      path: "/user/:userId/user_emails/:id/edit",
      name: "Edit User Email",
      component: UserEmailEdit,
      beforeEnter: async (to, from, next) => {
        await UserStore.fetch(to.params.userId);
        next();
      }
    },
    {
      path: "/user/:userId/user_emails/new",
      name: "New User Email",
      component: UserEmailNew
    },

    {
      path: "/user/:userId/user_mobile_numbers/:id/edit",
      name: "Edit User Mobile Number",
      component: UserMobileNumberEdit,
      beforeEnter: async (to, from, next) => {
        await UserStore.fetch(to.params.userId);
        next();
      }
    },
    {
      path: "/user/:userId/user_mobile_numbers/new",
      name: "New User Mobile Number",
      component: UserMobileNumberNew
    },
    // Company
    {
      path: "/merchant/profile/new",
      name: "New company",
      component: CompanyNew
    },
    {
      path: "/merchant/:id/profile",
      name: "Edit Company",
      component: CompanyEdit
    },
    {
      path: "/payout/new",
      name: "New Payout",
      component: PayoutNew
    },

    // Merchant Account
    {
      path: "/merchant/:companyId/account",
      name: "Merchant Account Edit",
      component: MerchantAccountEdit
    },

    // Company Settings
    {
      path: "/merchant/:companyId/settings",
      name: "Settings Edit",
      component: SettingsEdit
    },

    // Empowerments
    {
      path: "/merchant/:companyId/team",
      name: "All Empowerments",
      component: EmpowermentIndex
    },
    {
      path: "/merchant/:companyId/team/new",
      name: "New Empowerment",
      component: EmpowermentNew
    },
    {
      path: "/merchant/:companyId/team/:id/edit",
      name: "Edit Empowerment",
      component: EmpowermentEdit
    },

    // Invoice Reports
    {
      path: "/invoices",
      name: "Revenues Receivables",
      component: InvoiceReportRevenuesReceivables
    },
    {
      path: "/expenses",
      name: "Expenses",
      component: InvoiceReportExpensesPayables
    }
  ],

  scrollBehavior (to, from, savedPosition) {
    if (savedPosition) {
      document.getElementById("scroll-mark").scrollTop = savedPosition.y;
    } else if (to.hash) {
      document.querySelector(to.hash).scrollIntoView();
      document.getElementById("scroll-mark").scrollTop -= 40;
    } else {
      document.getElementById("scroll-mark").scrollTop = 0;
    }

    return null;
  }
});

router.beforeEach(async (to, from, next) => {
  ProgressBarStore.methods.setLoadingTimer();

  if (!to.params.hash || Dashboard.currentUser) {
    try {
      await UserStore.fetchCurrentUser();
      await CompanyStore.ensureCurrentCompany();
      await SetupStore.ensureCurrentState();
    } catch (e) {
      if (e.message === "Unauthenticated") {
        document.location.href = "/login";
      }

      return;
    }
  }
  next();
});

router.afterEach((to, from) => {
  ProgressBarStore.methods.cancelLoadingTimer();
  SidebarStore.closeMobileSidebar();
});

export default router;
